import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { HardHat, Plus, PencilSimple, Trash, X, FileText, Receipt, Warning, CheckCircle, ArrowLeft, Upload, DownloadSimple, Robot, SpinnerGap, FilePdf } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { fmtDateFR, fmtDateTimeFR } from '../utils/dates';
import { LayoutToggle, useLayoutMode, getLayoutContainerProps } from '../components/ui/LayoutToggle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => { if (!iso) return '-'; try { return fmtDateFR(iso); } catch { return '-'; } };

const SubcontractorsPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [list, setList] = useState([]);
  const [layoutMode, setLayoutMode] = useLayoutMode('subcontractors', 'grid');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [docTypes, setDocTypes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [subTab, setSubTab] = useState('documents');
  const [showSc, setShowSc] = useState(false);
  const [editSc, setEditSc] = useState(null);
  const [scForm, setScForm] = useState({ name: '', siret: '', email: '', phone: '', address: '', contact_name: '', notes: '' });
  const [showDoc, setShowDoc] = useState(false);
  const [docForm, setDocForm] = useState({ doc_type: 'kbis', expiry_date: '', file: null });
  const [showInv, setShowInv] = useState(false);
  const [invForm, setInvForm] = useState({ number: '', date: new Date().toISOString().slice(0, 10), amount_ht: 0, tva_rate: 20, project_id: '', description: '' });
  const [invFile, setInvFile] = useState(null);
  const [analyzingInv, setAnalyzingInv] = useState(false);

  const fetchList = useCallback(async () => {
    try {
      const [lRes, dRes, pRes] = await Promise.all([
        axios.get(`${API_URL}/api/subcontractors`, { withCredentials: true }),
        axios.get(`${API_URL}/api/subcontractors/doc-types`, { withCredentials: true }),
        axios.get(`${API_URL}/api/projects`, { withCredentials: true }),
      ]);
      setList(lRes.data); setDocTypes(dRes.data); setProjects(pRes.data);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchList(); }, [fetchList]);

  const fetchDetails = async (id) => {
    try {
      const [docsRes, invsRes] = await Promise.all([
        axios.get(`${API_URL}/api/subcontractors/${id}/documents`, { withCredentials: true }),
        axios.get(`${API_URL}/api/subcontractors/${id}/invoices`, { withCredentials: true }),
      ]);
      setDocuments(docsRes.data); setInvoices(invsRes.data);
    } catch { toast.error('Erreur'); }
  };
  const selectSc = (sc) => { setSelected(sc); setSubTab('documents'); fetchDetails(sc.id); };

  // SC CRUD
  const openCreateSc = () => { setEditSc(null); setScForm({ name: '', siret: '', email: '', phone: '', address: '', contact_name: '', notes: '' }); setShowSc(true); };
  const openEditSc = (sc) => { setEditSc(sc); setScForm(sc); setShowSc(true); };
  const handleSubmitSc = async (e) => {
    e.preventDefault();
    try {
      if (editSc) await axios.put(`${API_URL}/api/subcontractors/${editSc.id}`, scForm, { withCredentials: true });
      else await axios.post(`${API_URL}/api/subcontractors`, scForm, { withCredentials: true });
      toast.success('OK'); setShowSc(false); fetchList();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };
  const handleDeleteSc = async (id) => {
    if (!window.confirm('Supprimer ce sous-traitant et toutes ses donnees ?')) return;
    try { await axios.delete(`${API_URL}/api/subcontractors/${id}`, { withCredentials: true }); toast.success('Supprime'); setSelected(null); fetchList(); }
    catch { toast.error('Erreur'); }
  };

  // Documents
  const handleUploadDoc = async (e) => {
    e.preventDefault();
    if (!docForm.file) return;
    const fd = new FormData();
    fd.append('file', docForm.file); fd.append('doc_type', docForm.doc_type); fd.append('expiry_date', docForm.expiry_date || '');
    try {
      await axios.post(`${API_URL}/api/subcontractors/${selected.id}/documents`, fd, { withCredentials: true });
      toast.success('Document ajoute');
      setShowDoc(false); setDocForm({ doc_type: 'kbis', expiry_date: '', file: null });
      fetchDetails(selected.id); fetchList();
    } catch { toast.error('Erreur upload'); }
  };
  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try { await axios.delete(`${API_URL}/api/subcontractors/${selected.id}/documents/${docId}`, { withCredentials: true }); toast.success('Supprime'); fetchDetails(selected.id); fetchList(); }
    catch { toast.error('Erreur'); }
  };

  // Invoices
  const openCreateInv = () => { setInvForm({ number: '', date: new Date().toISOString().slice(0, 10), amount_ht: 0, tva_rate: 20, project_id: '', description: '' }); setInvFile(null); setShowInv(true); };

  const handleAnalyzeInvPDF = async (file) => {
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Veuillez selectionner un fichier PDF');
      return;
    }
    setInvFile(file);
    setAnalyzingInv(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/invoice-analysis/analyze`, fd, { withCredentials: true });
      if (res.data.success && res.data.data) {
        const d = res.data.data;
        const updates = {};
        if (d.montant_ht != null) updates.amount_ht = parseFloat(d.montant_ht) || 0;
        if (d.taux_tva != null) updates.tva_rate = parseFloat(d.taux_tva) || 20;
        if (d.date_facture) updates.date = d.date_facture;
        if (d.numero_facture) updates.number = d.numero_facture;
        if (d.fournisseur) updates.description = `Facture ${d.numero_facture || ''} - ${d.fournisseur}`.trim();
        setInvForm(prev => ({ ...prev, ...updates }));
        toast.success('Facture analysee - champs pre-remplis');
      } else {
        toast.error(res.data.error || 'Analyse echouee, remplissez manuellement');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur analyse PDF');
    } finally {
      setAnalyzingInv(false);
    }
  };

  const handleSubmitInv = async (e) => {
    e.preventDefault();
    if (!invForm.project_id) return toast.error('Chantier obligatoire');
    try {
      const fd = new FormData();
      Object.entries(invForm).forEach(([k, v]) => fd.append(k, v));
      if (invFile) fd.append('file', invFile);
      await axios.post(`${API_URL}/api/subcontractors/${selected.id}/invoices`, fd, { withCredentials: true });
      toast.success('Facture ajoutee et comptabilisee dans le chantier');
      setShowInv(false); setInvFile(null); fetchDetails(selected.id); fetchList();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };
  const handleDeleteInv = async (invId) => {
    if (!window.confirm('Supprimer cette facture ? La depense chantier sera aussi supprimee.')) return;
    try { await axios.delete(`${API_URL}/api/subcontractors/${selected.id}/invoices/${invId}`, { withCredentials: true }); toast.success('Supprime'); fetchDetails(selected.id); fetchList(); }
    catch { toast.error('Erreur'); }
  };

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  // Detail view
  if (selected) {
    const sc = list.find(s => s.id === selected.id) || selected;
    return (
      <div data-testid="subcontractor-detail">
        <button className="btn btn-secondary btn-sm" onClick={() => setSelected(null)} style={{ marginBottom: '14px' }}><ArrowLeft size={14} /> Retour</button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h2 style={{ margin: 0 }}>{sc.name}</h2>
            <p style={{ margin: '4px 0 0', color: '#5E5F66', fontSize: '0.85rem' }}>{sc.email} {sc.phone ? `- ${sc.phone}` : ''} - SIRET: {sc.siret || '-'}</p>
            {sc.missing_documents && sc.missing_documents.length > 0 && (
              <div style={{ marginTop: '6px', padding: '6px 10px', backgroundColor: '#FFF3E0', color: '#E65100', fontSize: '0.8rem', borderRadius: '4px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Warning size={14} /> Documents manquants : {sc.missing_documents.join(', ')}</div>
            )}
          </div>
          {isAdmin && (
            <div style={{ display: 'flex', gap: '6px' }}>
              <button className="btn btn-secondary" onClick={() => openEditSc(sc)}><PencilSimple size={14} /> Modifier</button>
              <button className="btn btn-danger" onClick={() => handleDeleteSc(sc.id)}><Trash size={14} /></button>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #E2E2E6', marginBottom: '18px' }}>
          <button onClick={() => setSubTab('documents')} style={{ padding: '10px 18px', border: 'none', cursor: 'pointer', fontWeight: '600', background: 'transparent', borderBottom: subTab === 'documents' ? '2px solid #002FA7' : '2px solid transparent', color: subTab === 'documents' ? '#002FA7' : '#5E5F66', marginBottom: '-2px' }} data-testid="sc-tab-documents"><FileText size={14} /> Documents ({documents.length})</button>
          <button onClick={() => setSubTab('invoices')} style={{ padding: '10px 18px', border: 'none', cursor: 'pointer', fontWeight: '600', background: 'transparent', borderBottom: subTab === 'invoices' ? '2px solid #002FA7' : '2px solid transparent', color: subTab === 'invoices' ? '#002FA7' : '#5E5F66', marginBottom: '-2px' }} data-testid="sc-tab-invoices"><Receipt size={14} /> Factures ({invoices.length})</button>
        </div>

        {subTab === 'documents' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
              {isAdmin && <button className="btn btn-primary" onClick={() => setShowDoc(true)} data-testid="add-sc-doc-btn"><Plus size={14} /> Ajouter un document</button>}
            </div>
            {documents.length === 0 ? <div className="card" style={{ padding: '32px', textAlign: 'center', color: '#5E5F66' }}>Aucun document</div> : (
              <div className="card"><table className="data-table"><thead><tr><th>Type</th><th>Fichier</th><th>Expiration</th><th>Ajoute</th><th></th></tr></thead>
              <tbody>{documents.map(d => (
                <tr key={d.id}>
                  <td style={{ fontWeight: '600' }}>{d.doc_label}</td>
                  <td style={{ fontSize: '0.85rem' }}>{d.original_filename}</td>
                  <td>{fmtDate(d.expiry_date)}</td>
                  <td style={{ fontSize: '0.85rem', color: '#5E5F66' }}>{fmtDate(d.created_at)}</td>
                  <td><div style={{ display: 'flex', gap: '4px' }}><a href={`${API_URL}${d.url}`} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm"><DownloadSimple size={12} /></a>{isAdmin && <button className="btn btn-danger btn-sm" onClick={() => handleDeleteDoc(d.id)}><Trash size={12} /></button>}</div></td>
                </tr>
              ))}</tbody></table></div>
            )}
          </>
        )}

        {subTab === 'invoices' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
              {isAdmin && <button className="btn btn-primary" onClick={openCreateInv} data-testid="add-sc-invoice-btn"><Plus size={14} /> Ajouter une facture</button>}
            </div>
            {invoices.length === 0 ? <div className="card" style={{ padding: '32px', textAlign: 'center', color: '#5E5F66' }}>Aucune facture</div> : (
              <div className="card"><table className="data-table"><thead><tr><th>N Facture</th><th>Date</th><th>Chantier</th><th>Description</th><th>HT</th><th>TTC</th><th>Statut</th><th>PDF</th><th></th></tr></thead>
              <tbody>{invoices.map(i => (
                <tr key={i.id}>
                  <td style={{ fontWeight: '600' }}>{i.number || '-'}</td>
                  <td>{fmtDate(i.date)}</td>
                  <td>{i.project_name || '-'}</td>
                  <td style={{ fontSize: '0.85rem' }}>{i.description}</td>
                  <td>{(i.amount_ht || 0).toLocaleString('fr-FR')} EUR</td>
                  <td style={{ fontWeight: '600' }}>{(i.amount_ttc || 0).toLocaleString('fr-FR')} EUR</td>
                  <td><span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: i.status === 'payee' ? '#E8F5E9' : '#FFF3E0', color: i.status === 'payee' ? '#2E7D32' : '#E65100' }}>{i.status === 'payee' ? 'Payee' : 'A payer'}</span></td>
                  <td>{i.pdf_url ? <a href={`${API_URL}${i.pdf_url}`} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm" data-testid={`sc-inv-pdf-${i.id}`}><FilePdf size={14} /> Voir</a> : <span style={{ color: '#9E9E9E', fontSize: '0.75rem' }}>-</span>}</td>
                  <td>{isAdmin && <button className="btn btn-danger btn-sm" onClick={() => handleDeleteInv(i.id)}><Trash size={12} /></button>}</td>
                </tr>
              ))}</tbody></table></div>
            )}
          </>
        )}

        {showSc && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
            <div style={{ backgroundColor: 'white', borderRadius: '6px', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between' }}><h3 style={{ margin: 0 }}>{editSc ? 'Modifier' : 'Nouveau sous-traitant'}</h3><button onClick={() => setShowSc(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button></div>
              <form onSubmit={handleSubmitSc} style={{ padding: '20px' }}>
                <div className="form-group"><label className="form-label">Raison sociale *</label><input type="text" className="form-input" value={scForm.name} onChange={(e) => setScForm({ ...scForm, name: e.target.value })} required /></div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-group"><label className="form-label">SIRET</label><input type="text" className="form-input" value={scForm.siret} onChange={(e) => setScForm({ ...scForm, siret: e.target.value })} /></div>
                  <div className="form-group"><label className="form-label">Contact</label><input type="text" className="form-input" value={scForm.contact_name} onChange={(e) => setScForm({ ...scForm, contact_name: e.target.value })} /></div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-group"><label className="form-label">Email</label><input type="email" className="form-input" value={scForm.email} onChange={(e) => setScForm({ ...scForm, email: e.target.value })} /></div>
                  <div className="form-group"><label className="form-label">Telephone</label><input type="tel" className="form-input" value={scForm.phone} onChange={(e) => setScForm({ ...scForm, phone: e.target.value })} /></div>
                </div>
                <div className="form-group"><label className="form-label">Adresse</label><input type="text" className="form-input" value={scForm.address} onChange={(e) => setScForm({ ...scForm, address: e.target.value })} /></div>
                <div className="form-group"><label className="form-label">Notes</label><textarea className="form-input" rows="2" value={scForm.notes} onChange={(e) => setScForm({ ...scForm, notes: e.target.value })} /></div>
                <div style={{ display: 'flex', gap: '10px' }}><button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setShowSc(false)}>Annuler</button><button type="submit" className="btn btn-primary" style={{ flex: 1 }}>{editSc ? 'Enregistrer' : 'Creer'}</button></div>
              </form>
            </div>
          </div>
        )}

        {showDoc && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
            <div style={{ backgroundColor: 'white', borderRadius: '6px', width: '100%', maxWidth: '440px' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between' }}><h3 style={{ margin: 0 }}>Ajouter un document</h3><button onClick={() => setShowDoc(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button></div>
              <form onSubmit={handleUploadDoc} style={{ padding: '20px' }}>
                <div className="form-group"><label className="form-label">Type *</label>
                  <Select value={docForm.doc_type} onValueChange={(v) => setDocForm({ ...docForm, doc_type: v })}>
                    <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="z-[9999]">{docTypes.map(dt => <SelectItem key={dt.key} value={dt.key}>{dt.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="form-group"><label className="form-label">Date d'expiration</label><input type="date" className="form-input" value={docForm.expiry_date} onChange={(e) => setDocForm({ ...docForm, expiry_date: e.target.value })} /></div>
                <div className="form-group"><label className="form-label">Fichier *</label><input type="file" className="form-input" onChange={(e) => setDocForm({ ...docForm, file: e.target.files[0] })} required data-testid="doc-file-input" /></div>
                <div style={{ display: 'flex', gap: '10px' }}><button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setShowDoc(false)}>Annuler</button><button type="submit" className="btn btn-primary" style={{ flex: 1 }}><Upload size={14} /> Uploader</button></div>
              </form>
            </div>
          </div>
        )}

        {showInv && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
            <div style={{ backgroundColor: 'white', borderRadius: '6px', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between' }}><h3 style={{ margin: 0 }}>Facture sous-traitant</h3><button onClick={() => setShowInv(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button></div>
              <form onSubmit={handleSubmitInv} style={{ padding: '20px' }}>
                <div style={{ padding: '10px', backgroundColor: '#FFF3E0', borderRadius: '4px', marginBottom: '14px', fontSize: '0.85rem', color: '#E65100' }}>Cette facture sera automatiquement comptabilisee dans les depenses du chantier choisi.</div>

                {/* PDF Analysis + Upload */}
                <div className="card" style={{ padding: '12px', marginBottom: '14px', backgroundColor: '#F8F9FF', border: '1px dashed #90CAF9' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <Robot size={18} weight="duotone" style={{ color: '#1565C0' }} />
                    <span style={{ fontWeight: '600', fontSize: '0.85rem', color: '#1565C0' }}>Importer la facture PDF (analyse IA)</span>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', opacity: analyzingInv ? 0.5 : 1 }}>
                      {analyzingInv ? <SpinnerGap size={14} className="spin" /> : <Upload size={14} />}
                      {analyzingInv ? 'Analyse...' : (invFile ? 'Changer le PDF' : 'Importer une facture PDF')}
                      <input type="file" accept=".pdf" style={{ display: 'none' }} disabled={analyzingInv}
                        onChange={(e) => { if (e.target.files[0]) handleAnalyzeInvPDF(e.target.files[0]); e.target.value = ''; }}
                        data-testid="sc-analyze-pdf-input" />
                    </label>
                    {invFile && <span style={{ fontSize: '0.8rem', color: '#2E7D32', display: 'flex', alignItems: 'center', gap: '4px' }}><FilePdf size={14} /> {invFile.name}</span>}
                    {!invFile && <span style={{ fontSize: '0.75rem', color: '#5E5F66' }}>Gemini pre-remplit N facture, date et montants</span>}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-group"><label className="form-label">N Facture</label><input type="text" className="form-input" value={invForm.number} onChange={(e) => setInvForm({ ...invForm, number: e.target.value })} data-testid="sc-inv-number" /></div>
                  <div className="form-group"><label className="form-label">Date *</label><input type="date" className="form-input" value={invForm.date} onChange={(e) => setInvForm({ ...invForm, date: e.target.value })} required /></div>
                </div>
                <div className="form-group"><label className="form-label">Chantier de rattachement <span style={{ color: '#C62828' }}>*</span></label>
                  <Select value={invForm.project_id} onValueChange={(v) => setInvForm({ ...invForm, project_id: v })}>
                    <SelectTrigger data-testid="sc-inv-project"><SelectValue placeholder="Selectionner un chantier" /></SelectTrigger>
                    <SelectContent className="z-[9999]">{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-group"><label className="form-label">Montant HT *</label><input type="number" step="0.01" className="form-input" value={invForm.amount_ht} onChange={(e) => setInvForm({ ...invForm, amount_ht: parseFloat(e.target.value) || 0 })} required /></div>
                  <div className="form-group"><label className="form-label">TVA %</label><input type="number" step="0.1" className="form-input" value={invForm.tva_rate} onChange={(e) => setInvForm({ ...invForm, tva_rate: parseFloat(e.target.value) || 20 })} /></div>
                </div>
                <div className="form-group"><label className="form-label">Description</label><textarea className="form-input" rows="2" value={invForm.description} onChange={(e) => setInvForm({ ...invForm, description: e.target.value })} /></div>
                <div style={{ display: 'flex', gap: '10px' }}><button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setShowInv(false)}>Annuler</button><button type="submit" className="btn btn-primary" style={{ flex: 1 }}>Ajouter</button></div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  // List view
  return (
    <div data-testid="subcontractors-page">
      <div className="page-header">
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><HardHat size={26} weight="bold" /> Sous-traitance</h1>
        <p className="page-subtitle">Gestion des sous-traitants, documents obligatoires et factures</p>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: 8 }}>
        <LayoutToggle layout={layoutMode} setLayout={setLayoutMode} testidPrefix="sc-layout" />
        {isAdmin && <button className="btn btn-primary" onClick={openCreateSc} data-testid="add-sc-btn"><Plus size={16} /> Nouveau sous-traitant</button>}
      </div>

      {list.length === 0 ? (
        <div className="card" style={{ padding: '48px', textAlign: 'center', color: '#5E5F66' }}><HardHat size={48} style={{ opacity: 0.4, marginBottom: '12px' }} /><p>Aucun sous-traitant enregistre</p></div>
      ) : (
        (() => {
          const lp = getLayoutContainerProps(layoutMode, { cardMinWidth: '320px' });
          return (
            <div style={lp.style} className={lp.className || ''} data-testid={`sc-container-${layoutMode}`}>
          {list.map(sc => (
            <div key={sc.id} className="card" style={{ cursor: 'pointer' }} onClick={() => selectSc(sc)} data-testid={`sc-card-${sc.id}`}>
              <div style={{ padding: '16px 18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '8px', backgroundColor: sc.compliant ? '#E8F5E9' : '#FFF3E0', color: sc.compliant ? '#2E7D32' : '#E65100', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{sc.compliant ? <CheckCircle size={20} weight="bold" /> : <Warning size={20} weight="bold" />}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: '700', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sc.name}</div>
                  <div style={{ fontSize: '0.8rem', color: '#5E5F66' }}>{sc.email || sc.phone || '-'}</div>
                </div>
              </div>
              <div style={{ padding: '0 18px 12px', display: 'grid', gap: '4px', fontSize: '0.8rem' }}>
                <div>{sc.documents_count} document(s) {!sc.compliant && <span style={{ color: '#E65100' }}>- manquant: {sc.missing_documents.length}</span>}</div>
                <div>{sc.invoices_count} facture(s) - <strong>{fmtDateTimeFR(sc.total_amount_ht)} HT</strong></div>
              </div>
            </div>
          ))}
        </div>
          );
        })()
      )}

      {showSc && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '6px', width: '100%', maxWidth: '520px' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between' }}><h3 style={{ margin: 0 }}>Nouveau sous-traitant</h3><button onClick={() => setShowSc(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button></div>
            <form onSubmit={handleSubmitSc} style={{ padding: '20px' }}>
              <div className="form-group"><label className="form-label">Raison sociale *</label><input type="text" className="form-input" value={scForm.name} onChange={(e) => setScForm({ ...scForm, name: e.target.value })} required data-testid="sc-name-input" /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group"><label className="form-label">SIRET</label><input type="text" className="form-input" value={scForm.siret} onChange={(e) => setScForm({ ...scForm, siret: e.target.value })} /></div>
                <div className="form-group"><label className="form-label">Email</label><input type="email" className="form-input" value={scForm.email} onChange={(e) => setScForm({ ...scForm, email: e.target.value })} /></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group"><label className="form-label">Telephone</label><input type="tel" className="form-input" value={scForm.phone} onChange={(e) => setScForm({ ...scForm, phone: e.target.value })} /></div>
                <div className="form-group"><label className="form-label">Contact</label><input type="text" className="form-input" value={scForm.contact_name} onChange={(e) => setScForm({ ...scForm, contact_name: e.target.value })} /></div>
              </div>
              <div className="form-group"><label className="form-label">Adresse</label><input type="text" className="form-input" value={scForm.address} onChange={(e) => setScForm({ ...scForm, address: e.target.value })} /></div>
              <div style={{ display: 'flex', gap: '10px' }}><button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setShowSc(false)}>Annuler</button><button type="submit" className="btn btn-primary" style={{ flex: 1 }} data-testid="submit-sc-btn">Creer</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubcontractorsPage;
